import * as THREE from "three";
import { getClass } from "@mmo/shared";
import { buildWeapon } from "./weapons";

/**
 * First-person view model: the weapon the local player sees in their own hands.
 * Attached as a child of the camera so it stays at a fixed screen position.
 *
 * Rendering: depthTest is OFF + high renderOrder so walls never clip it.
 * Geometry is shared with the third-person weapon meshes (cached in weapons.ts),
 * but we use a separate material so we can disable depthTest without affecting
 * third-person rendering.
 */

const VIEW_MATERIAL = new THREE.MeshLambertMaterial({
  vertexColors: true,
  depthTest: false,
  depthWrite: false,
});

const REST_POS = { x: 0.28, y: -0.26, z: -0.42 };
const REST_ROT = { x: -0.05, y: -0.18, z: 0 };

const FIRE_KICK_Z = 0.08;
const FIRE_KICK_PITCH = 0.15;
const FIRE_DUR = 110;

const MELEE_DUR = 320;

type Slot = "primary" | "melee";

export class ViewModel {
  private camera: THREE.PerspectiveCamera;
  private group: THREE.Group;
  private weapons: Partial<Record<Slot, THREE.Mesh>> = {};
  private classId = "";
  private fireUntil = 0;
  private meleeUntil = 0;
  private bobPhase = 0;

  constructor(camera: THREE.PerspectiveCamera) {
    this.camera = camera;
    this.group = new THREE.Group();
    camera.add(this.group);
  }

  setClass(classId: string) {
    if (classId === this.classId) return;
    this.classId = classId;
    const cls = getClass(classId);
    this.replace("primary", cls.primary.name);
    this.replace("melee", cls.melee.name);
  }

  private replace(slot: Slot, weaponName: string) {
    const old = this.weapons[slot];
    if (old) this.group.remove(old);
    const m = buildWeapon(weaponName);
    m.material = VIEW_MATERIAL;
    m.renderOrder = 1000;
    m.frustumCulled = false;
    if (slot === "melee") m.visible = false;
    this.group.add(m);
    this.weapons[slot] = m;
  }

  triggerFire() { this.fireUntil = performance.now() + FIRE_DUR; }
  triggerMelee() { this.meleeUntil = performance.now() + MELEE_DUR; }

  setVisible(v: boolean) { this.group.visible = v; }

  update(dt: number, hSpeed: number, onGround: boolean) {
    // weapon sway/bob — this is where the "feeling of running" lives now
    if (onGround && hSpeed > 0.5) {
      this.bobPhase += dt * (hSpeed * 0.9 + 2);
    } else {
      this.bobPhase += dt * 0.6;
    }
    const bobAmp = onGround ? Math.min(hSpeed / 7, 1) * 0.020 : 0.004;
    const bobX = Math.sin(this.bobPhase) * bobAmp * 0.6;
    const bobY = Math.sin(this.bobPhase * 2) * bobAmp;

    const now = performance.now();
    const fireT = Math.max(0, (this.fireUntil - now) / FIRE_DUR);
    const meleeT = Math.max(0, (this.meleeUntil - now) / MELEE_DUR);
    const fireKick = fireT * fireT; // ease-out kick
    const meleeArc = Math.sin((1 - meleeT) * Math.PI); // 0 → 1 → 0 across the swing

    // toggle which weapon is shown during melee swing
    const inMelee = meleeT > 0;
    if (this.weapons.primary) this.weapons.primary.visible = !inMelee;
    if (this.weapons.melee) this.weapons.melee.visible = inMelee;

    this.group.position.set(
      REST_POS.x + bobX - meleeArc * 0.5,
      REST_POS.y + bobY - meleeArc * 0.1,
      REST_POS.z + fireKick * FIRE_KICK_Z - meleeArc * 0.15,
    );
    this.group.rotation.set(
      REST_ROT.x + fireKick * FIRE_KICK_PITCH - meleeArc * 0.7,
      REST_ROT.y - meleeArc * 0.4,
      REST_ROT.z + meleeArc * 0.5,
    );
  }

  dispose() {
    this.camera.remove(this.group);
  }
}
